import React from 'react';
import { Box, Grid2, Typography, Container, Paper, Button, useTheme, Icon, Link } from '@mui/material';
import { useNavigate } from 'react-router-dom';

const TerminosCondiciones = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const handleVolverInicio = () => {
    navigate('/');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const sections = [
    {
      id: "aceptacion",
      title: "1. Aceptación de los Términos",
      icon: "gavel",
      content: (
        <Typography sx={{ color: theme.palette.text.secondary, lineHeight: 1.7 }}>
          Al descargar, acceder o utilizar <strong>Mi Rifa App</strong>, usted acepta estar sujeto a estos Términos y Condiciones, así como a nuestra Política de Privacidad. Si no está de acuerdo con alguna parte de estos términos, no debe utilizar la aplicación.
        </Typography>
      )
    },
    {
      id: "descripcion",
      title: "2. Descripción del Servicio",
      icon: "apps",
      content: (
        <>
          <Typography sx={{ mb: 2, color: theme.palette.text.secondary, lineHeight: 1.7 }}>
            <strong>Mi Rifa App</strong> es una plataforma tecnológica desarrollada por <strong>RAUH TECH LTDA</strong> que permite a los usuarios crear, administrar y comercializar boletos de rifas de manera digital.
          </Typography>
          <Box sx={{ p: 2, bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.03)' : '#f8fafc', borderRadius: '10px', border: theme.palette.mode === 'dark' ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid #e2e8f0' }}>
            <Typography variant="body2" sx={{ fontWeight: 700, color: theme.palette.mode === 'dark' ? '#818cf8' : theme.palette.primary.main, mb: 0.5 }}>
              Aclaración de Responsabilidad:
            </Typography>
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.6 }}>
              Mi Rifa App actúa únicamente como intermediario tecnológico. Los organizadores de las rifas son los únicos responsables de la legalidad, validez y cumplimiento en la entrega de los premios prometidos.
            </Typography>
          </Box>
        </>
      )
    },
    {
      id: "registro",
      title: "3. Registro y Cuentas de Usuario",
      icon: "person_add",
      content: (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Typography sx={{ color: theme.palette.text.secondary, lineHeight: 1.7 }}>
            Para utilizar ciertas funciones de la app, es necesario registrarse proporcionando información veraz y actualizada.
          </Typography>
          {[
            "Usted es responsable de mantener la confidencialidad de sus credenciales de acceso.",
            "Cualquier actividad realizada bajo su cuenta será de su exclusiva responsabilidad.",
            "Nos reservamos el derecho de suspender cuentas que incumplan estos términos o cometan fraude."
          ].map((text, i) => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1, bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.03)' : '#f8fafc', borderRadius: '8px', border: theme.palette.mode === 'dark' ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid #e2e8f0' }}>
              <Box sx={{ width: 22, height: 22, borderRadius: '6px', bgcolor: theme.palette.mode === 'dark' ? 'rgba(129, 140, 248, 0.15)' : 'rgba(45, 52, 132, 0.08)', color: theme.palette.mode === 'dark' ? '#818cf8' : theme.palette.primary.main, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', fontWeight: 900, flexShrink: 0 }}>
                {i + 1}
              </Box>
              <Typography variant="body2" sx={{ color: theme.palette.text.primary, fontWeight: 600 }}>
                {text}
              </Typography>
            </Box>
          ))}
        </Box>
      )
    },
    {
      id: "obligaciones",
      title: "4. Obligaciones de los Organizadores",
      icon: "assignment_turned_in",
      content: (
        <Grid2 container spacing={2}>
          {[
            { title: "Cumplimiento Legal", desc: "Asegurarse de cumplir con las leyes locales y autorizaciones pertinentes para la realización de sorteos." },
            { title: "Transparencia", desc: "Publicar premios reales, reglas claras, fecha fidedigna y lotería oficial del sorteo." },
            { title: "Entrega de Premios", desc: "Entregar los premios de forma oportuna a los ganadores según las condiciones pactadas." },
            { title: "Atención al Comprador", desc: "Responder a las consultas y solicitudes de los compradores de boletos." }
          ].map((item, index) => (
            <Grid2 size={{ xs: 12, sm: 6 }} key={index}>
              <Box sx={{ p: 2, borderRadius: '10px', bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.03)' : '#f8fafc', border: theme.palette.mode === 'dark' ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid #e2e8f0', height: '100%' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: theme.palette.mode === 'dark' ? '#818cf8' : theme.palette.primary.main, mb: 0.5 }}>
                  {item.title}
                </Typography>
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.5 }}>
                  {item.desc}
                </Typography>
              </Box>
            </Grid2>
          ))}
        </Grid2>
      )
    },
    {
      id: "propiedad",
      title: "5. Propiedad Intelectual",
      icon: "copyright",
      content: (
        <Typography sx={{ color: theme.palette.text.secondary, lineHeight: 1.7 }}>
          Todo el contenido, marcas comerciales, logotipos, código fuente y diseños de <strong>Mi Rifa App</strong> son propiedad exclusiva de <strong>RAUH TECH LTDA</strong> y están protegidos por las leyes de propiedad intelectual internacionales.
        </Typography>
      )
    },
    {
      id: "contacto",
      title: "6. Contacto y Soporte",
      icon: "mail",
      content: (
        <Box sx={{ p: 2.5, bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.03)' : '#f8fafc', borderRadius: '10px', border: theme.palette.mode === 'dark' ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid #e2e8f0' }}>
          <Typography sx={{ mb: 1, color: theme.palette.text.secondary, lineHeight: 1.7 }}>
            Si tiene alguna pregunta o inquietud respecto a estos Términos y Condiciones, puede comunicarse con nosotros a través de:
          </Typography>
          <Typography sx={{ color: theme.palette.text.primary, fontWeight: 700 }}>
            Correo:{' '}
            <Link href="mailto:administration@rauhtech.com" sx={{ color: theme.palette.mode === 'dark' ? '#818cf8' : theme.palette.primary.main, fontWeight: 700, textDecoration: 'none' }}>
              administration@rauhtech.com
            </Link>
          </Typography>
        </Box>
      )
    }
  ];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: theme.palette.background.default, color: theme.palette.text.primary }}>
      {/* Hero Header Corporativo */}
      <Box
        sx={{
          background:
            theme.palette.mode === 'dark'
              ? 'linear-gradient(180deg, #1e293b 0%, #0f172a 100%)'
              : 'linear-gradient(145deg, #1c215e 0%, #2d3484 100%)',
          color: 'white',
          pt: { xs: 12, md: 15 },
          pb: { xs: 6, md: 8 },
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            width: 220,
            height: 220,
            borderRadius: '50%',
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            top: -50,
            right: 40,
            pointerEvents: 'none',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            width: 120,
            height: 120,
            borderRadius: '50%',
            backgroundColor: 'rgba(255, 255, 255, 0.04)',
            bottom: -40,
            left: '20%',
            pointerEvents: 'none',
          }}
        />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Button
            onClick={handleVolverInicio}
            startIcon={<Icon>arrow_back</Icon>}
            sx={{
              color: 'white',
              fontWeight: 700,
              bgcolor: 'rgba(255, 255, 255, 0.12)',
              borderRadius: '8px',
              mb: 3,
              '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.22)' },
            }}
          >
            Volver al Inicio
          </Button>

          <Typography
            variant="h3"
            component="h1"
            sx={{
              fontWeight: 900,
              fontSize: { xs: '2rem', md: '2.8rem' },
              mb: 1,
              letterSpacing: '-0.02em',
            }}
          >
            Términos y Condiciones
          </Typography>

          <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '1.05rem', mb: 2 }}>
            Mi Rifa App • RAUH TECH LTDA
          </Typography>

          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.5, bgcolor: 'rgba(255, 255, 255, 0.1)', borderRadius: '6px' }}>
            <Icon sx={{ fontSize: 16, color: '#10b981' }}>schedule</Icon>
            <Typography variant="caption" sx={{ fontWeight: 700, color: 'rgba(255, 255, 255, 0.9)' }}>
              Última actualización: 19 de septiembre de 2024
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Contenido Principal */}
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
        {/* Tarjeta de Introducción */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 4 },
            mb: 4,
            borderRadius: '12px',
            bgcolor: theme.palette.background.paper,
            border: theme.palette.mode === 'dark' ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid #e2e8f0',
            boxShadow: theme.palette.mode === 'dark' ? '0 4px 20px rgba(0, 0, 0, 0.4)' : '0 2px 10px rgba(0, 0, 0, 0.03)',
          }}
        >
          <Typography sx={{ color: theme.palette.text.secondary, lineHeight: 1.8, mb: 2, fontSize: '1rem' }}>
            Bienvenido a <strong>Mi Rifa App</strong>, una plataforma desarrollada y operada por <strong>RAUH TECH LTDA</strong>. Al acceder o utilizar nuestra aplicación móvil y los servicios web asociados, usted acepta estar legalmente vinculado por los siguientes Términos y Condiciones.
          </Typography>
          <Typography sx={{ color: theme.palette.text.secondary, lineHeight: 1.8, fontSize: '1rem' }}>
            Por favor, lea estos términos detenidamente antes de comenzar a utilizar la plataforma. Si no está de acuerdo con alguno de los términos aquí expuestos, le solicitamos abstenerse de utilizar la aplicación.
          </Typography>
        </Paper>

        {/* Secciones */}
        {sections.map((section) => (
          <Paper
            key={section.id}
            elevation={0}
            sx={{
              p: { xs: 3, md: 4 },
              mb: 3.5,
              borderRadius: '12px',
              bgcolor: theme.palette.background.paper,
              border: theme.palette.mode === 'dark' ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid #e2e8f0',
              boxShadow: theme.palette.mode === 'dark' ? '0 4px 20px rgba(0, 0, 0, 0.4)' : '0 2px 10px rgba(0, 0, 0, 0.03)',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              {/* Outlined Icon Box */}
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: '10px',
                  bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.04)' : '#f8fafc',
                  border: theme.palette.mode === 'dark' ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid #e2e8f0',
                  color: theme.palette.primary.main,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <Icon sx={{ fontSize: 24, color: theme.palette.mode === 'dark' ? '#818cf8' : theme.palette.primary.main }}>{section.icon}</Icon>
              </Box>
              <Typography
                variant="h6"
                component="h2"
                sx={{
                  fontWeight: 800,
                  color: theme.palette.text.primary,
                  fontSize: { xs: '1.15rem', md: '1.3rem' },
                }}
              >
                {section.title}
              </Typography>
            </Box>

            {section.content}
          </Paper>
        ))}
      </Container>
    </Box>
  );
};

export default TerminosCondiciones;